import { useMutation } from "@apollo/client";
import { Box, Container, Text, VStack } from "@chakra-ui/react";
import { isNil } from "lodash";
import moment from "moment";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { connectModal, InjectedProps } from "redux-modal";
import { api } from "src/api";
import { MutationUpdateClientArgs } from "src/api/generated/types";
import { Modal } from "src/components/Modal";
import { Button, Input } from "src/components/styled";
import { Maybe } from "src/core";
import { useMyToast } from "src/hooks";
import { useTheme } from "src/hooks/useTheme";

type Props = InjectedProps & {
  onSuccess?: () => Promise<void>;
  defaultStartDate?: Maybe<Date>;
  defaultEndDate?: Maybe<Date>;
};

function _FiscalYearModal(props: Props) {
  const {
    handleHide,
    show: isVisible,
    defaultStartDate,
    defaultEndDate,
  } = props;

  const [startDate, setStartDate] = useState<null | string>(
    defaultStartDate
      ? moment(defaultStartDate).format("MM-DD")
      : moment(new Date("01-01")).format("MM-DD")
  );
  const [endDate, setEndDate] = useState<null | string>(
    defaultEndDate
      ? moment(defaultEndDate).format("MM-DD")
      : moment(new Date("12-31")).format("MM-DD")
  );
  const toast = useMyToast();
  const theme = useTheme();
  const { clientId } = useParams();

  const [updateClient] = useMutation(api.clients.update);

  const _onSubmit = async function () {
    try {
      if (!clientId) {
        toast.show({
          message: "Client not found",
          status: "error",
        });
        return;
      }

      if (isNil(startDate) || isNil(endDate)) {
        toast.show({
          message: "Please select both start and end dates",
          status: "error",
        });
        return;
      }

      // should be MM-DD
      const start = moment(startDate).format("MM-DD");
      const end = moment(endDate).format("MM-DD");

      const mutations: MutationUpdateClientArgs = {
        clientId,
        hasCustomFiscalYear: true,
        fiscalYearStart: start,
        fiscalYearEnd: end,
      };

      await updateClient({
        variables: mutations,
        refetchQueries: [api.clients.retrieve],
      });

      toast.show({
        message: `Successfully set fiscal year from ${start} to ${end}`,
        status: "success",
      });

      handleHide();

      if (props.onSuccess) {
        return await props.onSuccess();
      }
    } catch (err) {
      console.error(err);
      toast.show({
        message: (err as any)?.message || "Failed to set fiscal year",
        status: "error",
      });
    }
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);
    // Automatically set end date to 11 months after start date
    const [month, day] = newStartDate.split("-").map(Number);
    const newEndDate = moment()
      .month(month - 1)
      .date(day)
      .add(11, "months")
      .endOf("month")
      .format("MM-DD");
    setEndDate(newEndDate);
  };

  return (
    <Modal
      isVisible={isVisible}
      handleHide={handleHide}
      minH="35vh"
      Footer={
        <VStack style={{ width: "100%", marginBottom: 10 }}>
          <Button
            width="100%"
            variant="primary"
            onClick={_onSubmit}
            style={{ marginBottom: "1rem" }}
          >
            Set Fiscal Year
          </Button>
        </VStack>
      }
    >
      <Container
        padding="3rem 0 1rem 0"
        style={{
          alignItems: "center",
          textAlign: "center",
        }}
        marginTop="0px !important"
      >
        <Text
          color={theme.header}
          fontSize="xl"
          fontWeight="bold"
          marginBottom="0"
          style={{ textAlign: "left" }}
        >
          Set Custom Fiscal Year
        </Text>

        <Box style={{ marginTop: 25 }}>
          <Text
            style={{
              marginBottom: 20,
              textAlign: "left",
            }}
            color={theme.text}
          >
            Select your fiscal year period. The end date will automatically be
            set to 12 months after the start date. If you use the normal fiscal
            year of 1/1 - 12/31, you don't need this (so you can exit out of the
            modal).
          </Text>

          <Text
            style={{
              marginBottom: 10,
              textAlign: "left",
              fontWeight: "bold",
            }}
            color={theme.text}
          >
            Fiscal Year Start Date (MM-DD):
          </Text>

          <Input
            type="text"
            value={startDate || ""}
            onChange={handleStartDateChange}
            autoFocus
            placeholder="MM-DD"
            style={{ marginBottom: "20px" }}
          />

          <Text
            style={{
              marginBottom: 10,
              textAlign: "left",
              fontWeight: "bold",
            }}
            color={theme.text}
          >
            Fiscal Year End Date (MM-DD):
          </Text>

          <Input
            type="text"
            isDisabled={true}
            value={endDate || ""}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="MM-DD"
          />

          <br />

          <Text
            style={{
              marginTop: 5,
              textAlign: "left",
              fontSize: "14px",
            }}
            color={theme.text}
          >
            Note: The fiscal year period will be used for tax calculations and
            reporting.
          </Text>
        </Box>
      </Container>
    </Modal>
  );
}

export const FiscalYearModal = connectModal({
  name: "FiscalYearModal",
})(_FiscalYearModal);
